import React, { useEffect, useRef, useState } from 'react';
import { FaArrowRight, FaRobot, FaTimes } from 'react-icons/fa';

function ChatbotPanel({ onSend }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Ask about projects, APKs, IoT systems, AI work, or how to hire Nwachukwu Davis.',
      suggestions: [],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const threadRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  // Track unread count when panel is closed
  useEffect(() => {
    if (!open && messages.length > 1) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        setUnread((n) => n + 1);
      }
    }
  }, [messages, open]);

  // Clear unread on open
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const message = input.trim();
    if (!message || loading) return;

    setMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: 'user',
        content: message,
        suggestions: [],
      },
    ]);
    setInput('');
    setLoading(true);

    try {
      const response = await onSend(message);
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.reply,
          suggestions: response.suggestedItems || [],
        },
      ]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: error.message || 'The portfolio assistant is unavailable right now.',
          suggestions: [],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-widget">
      {/* Expanded panel */}
      <div
        className={open ? 'chat-widget__panel chat-widget__panel--open' : 'chat-widget__panel'}
        role="dialog"
        aria-label="Portfolio assistant"
        aria-hidden={!open}
      >
        <div className="chat-widget__header">
          <div className="chat-widget__header-info">
            <span className="chat-widget__avatar">
              <FaRobot aria-hidden="true" />
            </span>
            <div>
              <strong>Portfolio Assistant</strong>
              <span>Ask about projects, skills, or hiring</span>
            </div>
          </div>
          <button
            type="button"
            className="chat-widget__close"
            onClick={() => setOpen(false)}
            aria-label="Close chat"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="chat-widget__thread" ref={threadRef} aria-live="polite">
          {messages.map((message) => (
            <div
              key={message.id}
              className={
                message.role === 'assistant'
                  ? 'chat-widget__message chat-widget__message--assistant'
                  : 'chat-widget__message chat-widget__message--user'
              }
            >
              <div className="chat-widget__bubble">
                {message.role === 'assistant' ? (
                  <span className="chat-widget__bot-icon">
                    <FaRobot aria-hidden="true" />
                  </span>
                ) : null}
                <span>{message.content}</span>
              </div>

              {message.suggestions?.length ? (
                <div className="chat-widget__suggestions">
                  {message.suggestions.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={() => setOpen(false)}
                    >
                      {item.title}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          ))}

          {loading ? (
            <div className="chat-widget__message chat-widget__message--assistant">
              <div className="chat-widget__bubble">
                <span className="chat-widget__bot-icon">
                  <FaRobot aria-hidden="true" />
                </span>
                <span className="chat-widget__typing">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <form className="chat-widget__form" onSubmit={handleSubmit}>
          <input
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="Ask about POS, APKs, ESP32, AI, Web3..."
            disabled={loading}
          />
          <button type="submit" disabled={loading} aria-label="Send message">
            <FaArrowRight aria-hidden="true" />
          </button>
        </form>
      </div>

      {/* Floating trigger bubble */}
      <button
        type="button"
        className="chat-widget__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close assistant' : 'Open portfolio assistant'}
        aria-expanded={open}
      >
        {open ? <FaTimes aria-hidden="true" /> : <FaRobot aria-hidden="true" />}
        {unread > 0 && !open ? (
          <span className="chat-widget__badge" aria-label={`${unread} unread messages`}>
            {unread}
          </span>
        ) : null}
      </button>
    </div>
  );
}

export default ChatbotPanel;
